// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/challenges/:id\",\"regex\":\"^/challenges/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/missions/:id\",\"regex\":\"^/missions/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/missions/detail/:id\",\"regex\":\"^/missions/detail/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/profile/:id\",\"regex\":\"^/profile/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/submissions/:submissionId\",\"regex\":\"^/submissions/([^/]+)$\",\"paramNames\":[\"submissionId\"],\"hasOptionalPrefix\":false},{\"path\":\"/submit/:challengeId\",\"regex\":\"^/submit/([^/]+)$\",\"paramNames\":[\"challengeId\"],\"hasOptionalPrefix\":false},{\"path\":\"/submit/:challengeId/success\",\"regex\":\"^/submit/([^/]+)/success$\",\"paramNames\":[\"challengeId\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/onboarding/home-icon\"},{\"path\":\"/onboarding/tutorial\"},{\"path\":\"/saved\"},{\"path\":\"/auth/confirm\"},{\"path\":\"/auth/verify-email\"},{\"path\":\"/challenges\"},{\"path\":\"/challenges/onboarding/home-icon\"},{\"path\":\"/challenges/onboarding/tutorial\"},{\"path\":\"/challenges/saved\"},{\"path\":\"/events/block-party\"},{\"path\":\"/events/ugcevent\"},{\"path\":\"/events/ugcevent/qr\"},{\"path\":\"/exchange-notice-details\"},{\"path\":\"/leaderboard\"},{\"path\":\"/login\"},{\"path\":\"/missions\"},{\"path\":\"/missions/onboarding/home-icon\"},{\"path\":\"/missions/onboarding/tutorial\"},{\"path\":\"/missions/saved\"},{\"path\":\"/mypage\"},{\"path\":\"/mypage/exchange\"},{\"path\":\"/mypage/exchange/history\"},{\"path\":\"/mypage/point-history\"},{\"path\":\"/mypage/settings\"},{\"path\":\"/mypage/settings/email\"},{\"path\":\"/notifications\"},{\"path\":\"/policy/reward-v2\"},{\"path\":\"/submit\"},{\"path\":\"/test/tiktok-embed\"},{\"path\":\"/test-animation\"},{\"path\":\"/tiktok-required\"},{\"path\":\"/welcome\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.1.4";import * as Sentry from '@sentry/nextjs';
import { supabaseIntegration } from '@supabase/sentry-js-integration';
import { SupabaseClient } from '@supabase/supabase-js';

/** 에러 객체에서 메시지 문자열 추출 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return String(error ?? '');
}

// 수집 제외할 에러 패턴 (노이즈 감소)
const IGNORED_ERRORS = [
  // 청크 로드 에러 (네트워크 불안정, 배포 직후 캐시 문제)
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  // 네트워크 에러 (사용자 환경 문제)
  'NetworkError',
  'Failed to fetch',
  'Load failed',
  'Network request failed',
  // 브라우저 확장 프로그램 관련
  'ResizeObserver loop',
  // 취소된 요청
  'AbortError',
  'The operation was aborted',
  // 사용자가 페이지를 떠남
  'Navigation cancelled',
];

/**
 * 로컬 환경인지 확인
 * @returns 로컬 환경이면 true
 */
function isLocalEnvironment(): boolean {
  // 브라우저 환경 체크
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    const isLocalhost =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
    const isNgrok = hostname.includes('ngrok');

    if (isLocalhost || isNgrok) {
      return true;
    }
  }

  // 개발 환경 체크
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  return false;
}

// 로컬 환경에서는 Sentry 초기화 건너뛰기
let onRouterTransitionStart: typeof Sentry.captureRouterTransitionStart;

if (isLocalEnvironment()) {
  // 로컬 환경에서는 Sentry를 초기화하지 않음
  // 빈 함수로 설정하여 타입 에러 방지
  onRouterTransitionStart = () => {};
} else {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Add optional integrations for additional features
    integrations: [
      // Browser 프로파일링: 브라우저에서 함수 실행 시간 측정 및 성능 병목 식별
      Sentry.browserProfilingIntegration(),
      // Supabase 통합: Supabase 클라이언트 에러 자동 추적
      supabaseIntegration(SupabaseClient, Sentry, {
        tracing: true,
        breadcrumbs: true,
        errors: true,
      }),
      // Browser Tracing: Supabase URL 제외하여 중복 스팬 방지
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest: url => {
          const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
          // 빈 문자열이나 공백만 있는 경우 모든 요청 추적
          if (!supabaseUrl?.trim()) {
            return true;
          }
          // Supabase REST API 호출은 제외 (supabaseIntegration이 처리)
          return !url.startsWith(`${supabaseUrl}/rest`);
        },
      }),
      // Replay Integration: 로컬 환경에서는 초기화 자체를 건너뛰므로 항상 추가
      Sentry.replayIntegration({
        // 텍스트 마스킹 및 미디어 차단 비활성화: 디버깅을 위해 전체 콘텐츠 캡처
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1,
    // Profiling: 브라우저에서 느린 코드 경로를 찾기 위한 샘플링 레이트
    // 프로파일링은 CPU 집약적이므로 낮은 샘플링 레이트 권장
    profilesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1,
    // Enable logs to be sent to Sentry (프로덕션에서는 비용 절감을 위해 비활성화)
    enableLogs: process.env.NODE_ENV !== 'production',

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // PII(개인 식별 정보) 자동 전송 비활성화 (GDPR/CCPA 준수)
    // 필요한 사용자 정보는 Sentry.setUser()로 명시적으로 전송
    // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
    sendDefaultPii: false,

    // 에러 필터링: 불필요한 에러 제외
    beforeSend(event, hint) {
      const errorMessage = getErrorMessage(hint.originalException);

      // 무시할 에러 패턴 체크
      const shouldIgnore = IGNORED_ERRORS.some(pattern => errorMessage.includes(pattern));

      if (shouldIgnore) {
        return null; // 이벤트 전송 안 함
      }

      // 민감한 헤더 제거 (대소문자 모두 처리)
      if (event.request?.headers) {
        const headers = event.request.headers as Record<string, unknown>;
        delete headers.Authorization;
        delete headers.authorization;
        delete headers.Cookie;
        delete headers.cookie;
      }

      return event;
    },

    // URL 기반 에러 필터링 (특정 페이지 제외)
    denyUrls: [
      // 브라우저 확장 프로그램
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  // 글로벌 태그 설정: 모든 이벤트에 공통 정보 추가
  Sentry.setTag('platform', 'web');
  Sentry.setTag('runtime', 'client');
  Sentry.setTag(
    'environment',
    process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.NODE_ENV || 'development',
  );

  // 앱 버전 (배포 시 설정)
  if (process.env.NEXT_PUBLIC_APP_VERSION) {
    Sentry.setTag('app.version', process.env.NEXT_PUBLIC_APP_VERSION);
  }

  // Vercel 배포 정보
  if (process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA) {
    Sentry.setTag('commit', process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA.slice(0, 7));
  }

  onRouterTransitionStart = Sentry.captureRouterTransitionStart;
}

export { onRouterTransitionStart };
